import React, { memo, useState, useEffect, useRef } from 'react';
import { Handle, Position, useNodeId, useReactFlow } from '@xyflow/react';
import { 
  Play, MessageSquare, Zap, PhoneForwarded, FormInput, MessageSquareText, 
  GitBranch, Hash, UserPlus, FileText, Hourglass, Clock, CreditCard, StopCircle, ChevronRight
} from 'lucide-react';

const nodeIcons = {
  start: { icon: <Play size={10} />, color: '#10b981', label: 'Start (Greeting)' },
  conversation: { icon: <MessageSquare size={10} />, color: '#ec4899', label: 'Conversation' },
  function: { icon: <Zap size={10} />, color: '#a855f7', label: 'Function' },
  call_transfer: { icon: <PhoneForwarded size={10} />, color: '#f59e0b', label: 'Call Transfer' },
  collect_input: { icon: <FormInput size={10} />, color: '#f59e0b', label: 'Collect Input' },
  send_sms: { icon: <MessageSquareText size={10} />, color: '#14b8a6', label: 'Send SMS' },
  logic_split: { icon: <GitBranch size={10} />, color: '#6366f1', label: 'Logic Split' },
  press_digit: { icon: <Hash size={10} />, color: '#3b82f6', label: 'Press Digit' },
  agent_transfer: { icon: <UserPlus size={10} />, color: '#f97316', label: 'Agent Transfer' },
  extract_variable: { icon: <FileText size={10} />, color: '#94a3b8', label: 'Extract Variable' },
  inbound_webhook: { icon: <Hourglass size={10} />, color: '#c084fc', label: 'Wait (Inbound Webhook)' },
  time_converter: { icon: <Clock size={10} />, color: '#0ea5e9', label: 'Time Converter' },
  stripe_payment: { icon: <CreditCard size={10} />, color: '#6366f1', label: 'Stripe Payment' },
  ending: { icon: <StopCircle size={10} />, color: '#22d3ee', label: 'Ending' },
};

const CustomNode = ({ type, data, isConnectable, selected }) => {
  const config = nodeIcons[type] || nodeIcons.conversation;
  const nodeId = useNodeId();
  const { getNode } = useReactFlow();
  const node = getNode(nodeId);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const textareaRef = useRef(null);

  const rawText = data.prompt !== undefined ? data.prompt : 
                  data.content !== undefined ? data.content : 
                  data.dialogue_text !== undefined ? data.dialogue_text : "";

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(rawText);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== rawText) {
      let keyToUpdate = 'content';
      if (data.prompt !== undefined) keyToUpdate = 'prompt';
      else if (data.dialogue_text !== undefined) keyToUpdate = 'dialogue_text';
      
      const event = new CustomEvent('nodeDataUpdate', {
        detail: { nodeId, key: keyToUpdate, value: editValue }
      });
      document.dispatchEvent(event);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  return (
    <div className={`detailed-node ${selected ? 'selected' : ''}`}>
      <Handle type="target" position={Position.Left} className="node-handle" isConnectable={isConnectable} />
      
      <div className="node-header" style={{ borderTop: `3px solid ${config.color}` }}>
        <div className="node-title">
          <div className="node-icon-small" style={{ backgroundColor: config.color }}>
            {React.cloneElement(config.icon, { size: 10, color: 'white' })}
          </div>
          <span>{data.label || node?.label || config.label}</span>
        </div>
        <div className="node-id-label">{data.id || `N_${type.toUpperCase()}`}</div>
      </div>

      <div className="node-body" onClick={handleEditClick} style={{ cursor: 'text' }}>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="script-editor"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Enter instruction here..."
          />
        ) : (
          <div className="script-preview" title="Click to edit">
            {rawText ? rawText : <span style={{opacity: 0.5}}>Click to add instruction...</span>}
          </div>
        )}
      </div>

      <div className="node-footer">
        <div className="transition-header">
          <ChevronRight size={10} /> Transitions
        </div>
        <div className="transition-list">
          {data.transitions && data.transitions.length > 0 ? (
            data.transitions.map((t, i) => (
              <div key={t.id || i} className="transition-item">
                <span className="transition-text">{t.condition || 'Default'}</span>
                <Handle 
                  type="source" 
                  position={Position.Right} 
                  id={t.id || `source-${i}`}
                  className="transition-handle"
                  isConnectable={isConnectable}
                />
              </div>
            ))
          ) : (
            <div className="transition-item">
              <span className="transition-text">Next Node</span>
              <Handle 
                type="source" 
                position={Position.Right} 
                id="default-source"
                className="transition-handle"
                isConnectable={isConnectable}
              />
            </div>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .detailed-node {
          background: #ffffff;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          width: 260px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          font-family: inherit;
          overflow: hidden;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .detailed-node:hover {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .detailed-node.selected {
          border-color: #3b82f6;
          border-width: 2px;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        .node-header {
          padding: 8px 10px;
          background: #fcfcfc;
          border-bottom: 1px solid #f1f5f9;
        }
        .node-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          margin-bottom: 4px;
        }
        .node-icon-small {
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }
        .node-id-label {
          font-size: 10px;
          color: #94a3b8;
          font-weight: 500;
          word-break: break-all;
        }
        .node-body {
          padding: 10px;
          background: #ffffff;
          min-height: 60px;
        }
        .script-preview {
          font-size: 11px;
          line-height: 1.5;
          color: #64748b;
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .script-editor {
          width: 100%;
          min-height: 60px;
          font-size: 11px;
          line-height: 1.5;
          color: #334155;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 6px;
          outline: none;
          resize: vertical;
          background: #f8fafc;
          font-family: inherit;
        }
        .script-editor:focus {
          border-color: #3b82f6;
          background: #ffffff;
        }
        .node-footer {
          border-top: 1px solid #f1f5f9;
          background: #f8fafc;
          padding: 8px 10px;
        }
        .transition-header {
          font-size: 10px;
          font-weight: 600;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 6px;
        }
        .transition-list {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .transition-item {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          padding: 5px 8px;
          position: relative;
          display: flex;
          align-items: center;
        }
        .transition-text {
          font-size: 10px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }
        .node-handle {
          background: #cbd5e1 !important;
          width: 8px !important;
          height: 8px !important;
          border: 1px solid white !important;
        }
        .transition-handle {
          right: -5px !important;
          background: #cbd5e1 !important;
          width: 8px !important;
          height: 8px !important;
          border: 1px solid white !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
        }
      `}} />
    </div>
  );
};

const types = Object.keys(nodeIcons);
export const reactFlowNodeTypes = types.reduce((acc, type) => {
  acc[type] = memo(CustomNode);
  return acc;
}, {});
